import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Plus, Edit2, ShieldCheck, Download, FileText, FileSpreadsheet, Trash2, Save, Search, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import SearchableSelect from '@/components/SearchableSelect';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Joinings() {
  const [activeTab, setActiveTab] = useState('joinings');
  const [joinings, setJoinings] = useState([]);
  const [closures, setClosures] = useState([]);
  const [open, setOpen] = useState(false);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [selectedJoining, setSelectedJoining] = useState(null);
  const [newStatus, setNewStatus] = useState('');
  const [user, setUser] = useState(null);
  const [filterCompany, setFilterCompany] = useState('all');
  const [filterRecruiter, setFilterRecruiter] = useState('all');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [search, setSearch] = useState('');
  const [formData, setFormData] = useState({ closure_id: '', joining_date: '', status: 'confirmed', remarks: '' });

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) setUser(JSON.parse(userData));
  }, []);

  const fetchJoinings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/joinings`, { headers: { Authorization: `Bearer ${token}` } });
      setJoinings(response.data);
    } catch { toast.error('Failed to fetch joinings'); }
  };

  const fetchClosures = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/closures`, { headers: { Authorization: `Bearer ${token}` } });
      setClosures(response.data);
    } catch { toast.error('Failed to fetch closures'); }
  };

  useEffect(() => { fetchJoinings(); fetchClosures(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/joinings`, formData, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Joining recorded'); setOpen(false);
      setFormData({ closure_id: '', joining_date: '', status: 'confirmed', remarks: '' }); fetchJoinings();
    } catch { toast.error('Failed to record joining'); }
  };

  const handleStatusUpdate = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/joinings/${selectedJoining.id}/status`, { status: newStatus }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Status updated'); setStatusDialogOpen(false); setSelectedJoining(null); fetchJoinings();
    } catch { toast.error('Failed to update status'); }
  };

  const handleValidate = async (joiningId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/joinings/${joiningId}/validate`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Joining validated & invoice created'); fetchJoinings();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to validate joining'); }
  };

  const isRecruiter = user?.role === 'recruiter';
  const isAdmin = user?.role === 'admin';
  const isAdminSupport = user?.role === 'admin_support';
  const canValidate = user && ['team_leader', 'admin', 'admin_support'].includes(user.role);
  const canExport = user && ['admin', 'admin_support', 'team_leader'].includes(user.role);
  const canSeeRevenue = isAdmin;
  const canSeeInvoicing = isAdmin;

  const handleDeleteJoining = async (id) => {
    if (!window.confirm('Are you sure you want to delete this joining?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/joinings/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Joining deleted'); fetchJoinings();
    } catch { toast.error('Failed to delete joining'); }
  };

  const handleExport = async (format) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/export/joinings?format=${format}`, { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' });
      const ext = format === 'pdf' ? 'pdf' : 'xlsx';
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a'); link.href = url; link.setAttribute('download', `joinings.${ext}`);
      document.body.appendChild(link); link.click(); link.remove(); window.URL.revokeObjectURL(url);
      toast.success(`Exported as ${format.toUpperCase()}`);
    } catch { toast.error('Failed to export joinings'); }
  };

  const uniqueCompanies = useMemo(() => [...new Set(joinings.map(j => j.client_name).filter(Boolean))].sort(), [joinings]);
  const uniqueRecruiters = useMemo(() => [...new Set(joinings.map(j => j.recruiter_name).filter(Boolean))].sort(), [joinings]);

  const filteredJoinings = useMemo(() => {
    let data = [...joinings];
    const q = search.trim().toLowerCase();
    if (q) {
      data = data.filter(j =>
        (j.candidate_name || '').toLowerCase().includes(q) ||
        (j.client_name || '').toLowerCase().includes(q) ||
        (j.position_title || '').toLowerCase().includes(q) ||
        (j.recruiter_name || '').toLowerCase().includes(q)
      );
    }
    if (filterCompany !== 'all') data = data.filter(j => j.client_name === filterCompany);
    if (filterRecruiter !== 'all') data = data.filter(j => j.recruiter_name === filterRecruiter);
    if (filterDateFrom) data = data.filter(j => (j.joining_date || j.created_at || '') >= filterDateFrom);
    if (filterDateTo) data = data.filter(j => (j.joining_date || j.created_at || '').slice(0, 10) <= filterDateTo);
    return data;
  }, [joinings, filterCompany, filterRecruiter, filterDateFrom, filterDateTo, search]);

  const clearFilters = () => { setFilterCompany('all'); setFilterRecruiter('all'); setFilterDateFrom(''); setFilterDateTo(''); setSearch(''); };

  const tabs = [
    { id: 'joinings', label: 'Joinings' },
    ...(canSeeInvoicing ? [{ id: 'invoicing', label: 'Invoicing' }] : []),
  ];

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-blue-900 mb-2" data-testid="joinings-title" style={{ fontFamily: 'Manrope, sans-serif' }}>Joinings</h1>
          <p className="text-slate-600">Track candidate onboarding & invoicing</p>
        </div>
        {activeTab === 'joinings' && (
          <div className="flex items-center gap-3">
            {canExport && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button data-testid="export-joinings-button" variant="outline" className="border-blue-200 text-blue-700 hover:bg-blue-50"><Download size={16} className="mr-2" />Export</Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem data-testid="export-joinings-excel" onClick={() => handleExport('excel')}><FileSpreadsheet size={16} className="mr-2" />Excel</DropdownMenuItem>
                  <DropdownMenuItem data-testid="export-joinings-pdf" onClick={() => handleExport('pdf')}><FileText size={16} className="mr-2" />PDF</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button data-testid="add-joining-button" className="bg-blue-600 hover:bg-blue-700 text-white rounded font-bold"><Plus size={16} className="mr-2" />Add Joining</Button>
              </DialogTrigger>
              <DialogContent className="rounded">
                <DialogHeader>
                  <DialogTitle className="text-2xl font-bold text-blue-900">Record Joining</DialogTitle>
                  <DialogDescription>Record a new candidate joining from a closure.</DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                  <div><Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Closure</Label>
                    <select data-testid="joining-closure-select" value={formData.closure_id} onChange={(e) => setFormData({ ...formData, closure_id: e.target.value })} required className="mt-2 flex h-10 w-full rounded border border-blue-200 bg-white px-3 py-2 text-sm sr-only">
                      <option value="">Select a closure</option>
                      {closures.map(c => <option key={c.id} value={c.id}>{c.candidate_name} - {c.position_title}</option>)}
                    </select>
                    <div className="mt-2">
                      <SearchableSelect value={formData.closure_id} onChange={(id) => setFormData({ ...formData, closure_id: id })}
                        items={closures} getLabel={(c) => c.candidate_name} getSublabel={(c) => c.position_title}
                        emptyLabel="Select a closure" placeholder="Search candidate or position…" testId="joining-closure" />
                    </div></div>
                  <div><Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Joining Date</Label>
                    <Input data-testid="joining-date-input" type="date" value={formData.joining_date} onChange={(e) => setFormData({ ...formData, joining_date: e.target.value })} required className="mt-2 rounded border-blue-200" /></div>
                  <div><Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Status</Label>
                    <select data-testid="joining-status-select" value={formData.status} onChange={(e) => setFormData({ ...formData, status: e.target.value })} className="mt-2 flex h-10 w-full rounded border border-blue-200 bg-white px-3 py-2 text-sm">
                      <option value="confirmed">Confirmed</option><option value="pending">Pending</option><option value="joined">Joined</option><option value="cancelled">Cancelled</option>
                    </select></div>
                  <div><Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Remarks</Label>
                    <textarea data-testid="joining-remarks-input" value={formData.remarks} onChange={(e) => setFormData({ ...formData, remarks: e.target.value })} className="mt-2 flex w-full rounded border border-blue-200 bg-white px-3 py-2 text-sm min-h-[60px]" placeholder="Optional remarks..." /></div>
                  <Button type="submit" data-testid="submit-joining-button" className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded font-bold">Record Joining</Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        )}
      </div>

      {/* Tabs */}
      {tabs.length > 1 && (
        <div className="flex gap-1 bg-slate-100 rounded-lg p-1 mb-4 w-fit" data-testid="joinings-tabs">
          {tabs.map(t => (
            <button key={t.id} data-testid={`tab-${t.id}`} onClick={() => setActiveTab(t.id)}
              className={`px-4 py-2 text-sm font-bold rounded-md transition-all ${activeTab === t.id ? 'bg-white text-blue-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
              {t.label}
            </button>
          ))}
        </div>
      )}

      {activeTab === 'joinings' && (
        <>
          {/* Filters */}
          <div className="bg-white border border-blue-100 rounded shadow-sm p-4 mb-4 flex flex-wrap items-end gap-4">
            <div className="flex-1 min-w-[200px]">
              <Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Search</Label>
              <div className="relative mt-1">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input data-testid="filter-joining-search" type="search" value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Candidate, company, position, recruiter..."
                  className="pl-8 h-9 rounded border-blue-200 text-sm" />
              </div>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Company</Label>
              <select data-testid="filter-joining-company" value={filterCompany} onChange={(e) => setFilterCompany(e.target.value)} className="mt-1 flex h-9 rounded border border-blue-200 bg-white px-3 py-1 text-sm">
                <option value="all">All Companies</option>
                {uniqueCompanies.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Recruiter</Label>
              <select data-testid="filter-joining-recruiter" value={filterRecruiter} onChange={(e) => setFilterRecruiter(e.target.value)} className="mt-1 flex h-9 rounded border border-blue-200 bg-white px-3 py-1 text-sm">
                <option value="all">All Recruiters</option>
                {uniqueRecruiters.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-blue-900">From Date</Label>
              <Input data-testid="filter-joining-from" type="date" value={filterDateFrom} onChange={(e) => setFilterDateFrom(e.target.value)} className="mt-1 h-9 rounded border-blue-200 text-sm" />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-blue-900">To Date</Label>
              <Input data-testid="filter-joining-to" type="date" value={filterDateTo} onChange={(e) => setFilterDateTo(e.target.value)} className="mt-1 h-9 rounded border-blue-200 text-sm" />
            </div>
            <Button variant="ghost" size="sm" onClick={clearFilters} className="text-slate-500 text-xs">Clear Filters</Button>
            <span className="text-xs text-slate-500 ml-auto">{filteredJoinings.length} of {joinings.length} joinings</span>
          </div>

          {/* Status Update Dialog */}
          <Dialog open={statusDialogOpen} onOpenChange={setStatusDialogOpen}>
            <DialogContent className="rounded">
              <DialogHeader>
                <DialogTitle className="text-2xl font-bold text-blue-900">Update Joining Status</DialogTitle>
                <DialogDescription>Change the status of this joining record.</DialogDescription>
              </DialogHeader>
              {selectedJoining && (
                <form onSubmit={handleStatusUpdate} className="space-y-4 mt-4">
                  <div className="bg-blue-50 p-4 rounded border border-blue-200">
                    <p className="text-sm font-semibold text-blue-900">{selectedJoining.candidate_name}</p>
                    <p className="text-xs text-slate-600">{selectedJoining.position_title}</p>
                    <p className="text-xs text-slate-500 mt-1">Joining: {new Date(selectedJoining.joining_date).toLocaleDateString()}</p>
                  </div>
                  <div><Label className="text-xs font-bold uppercase tracking-wider text-blue-900">New Status</Label>
                    <select data-testid="joining-status-update-select" value={newStatus} onChange={(e) => setNewStatus(e.target.value)} className="mt-2 flex h-10 w-full rounded border border-blue-200 bg-white px-3 py-2 text-sm" required>
                      <option value="">Select status</option><option value="confirmed">Confirmed</option><option value="pending">Pending</option><option value="joined">Joined</option><option value="cancelled">Cancelled</option>
                    </select></div>
                  <Button type="submit" data-testid="submit-status-update-button" className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded font-bold">Update Status</Button>
                </form>
              )}
            </DialogContent>
          </Dialog>

          <div className="bg-white border border-blue-100 rounded shadow-sm overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-blue-50 border-b border-blue-100">
                  <TableHead className="font-bold text-blue-900">Candidate</TableHead>
                  <TableHead className="font-bold text-blue-900">Position</TableHead>
                  <TableHead className="font-bold text-blue-900">Client</TableHead>
                  <TableHead className="font-bold text-blue-900">Joining Date</TableHead>
                  {canSeeRevenue && <TableHead className="font-bold text-blue-900">Revenue</TableHead>}
                  <TableHead className="font-bold text-blue-900">Status</TableHead>
                  <TableHead className="font-bold text-blue-900">Validation</TableHead>
                  <TableHead className="font-bold text-blue-900">Recruiter</TableHead>
                  <TableHead className="font-bold text-blue-900">Remarks</TableHead>
                  <TableHead className="font-bold text-blue-900">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredJoinings.length === 0 ? (
                  <TableRow><TableCell colSpan={canSeeRevenue ? 10 : 9} className="text-center text-slate-500 py-8">No joinings found</TableCell></TableRow>
                ) : (
                  filteredJoinings.map((joining) => (
                    <TableRow key={joining.id} data-testid={`joining-row-${joining.id}`} className="hover:bg-blue-50">
                      <TableCell className="font-semibold text-blue-900">{joining.candidate_name}</TableCell>
                      <TableCell>{joining.position_title}</TableCell>
                      <TableCell className="text-sm">{joining.client_name || <span className="text-slate-400">-</span>}</TableCell>
                      <TableCell>{new Date(joining.joining_date).toLocaleDateString()}</TableCell>
                      {canSeeRevenue && (
                        <TableCell className="font-bold text-emerald-700">{joining.revenue ? `₹${joining.revenue.toLocaleString('en-IN')}` : <span className="text-slate-400">-</span>}</TableCell>
                      )}
                      <TableCell>
                        <span className={`px-2 py-1 text-xs font-bold uppercase rounded ${joining.status === 'joined' ? 'bg-emerald-100 text-emerald-800' : joining.status === 'confirmed' ? 'bg-cyan-100 text-cyan-800' : joining.status === 'pending' ? 'bg-amber-100 text-amber-800' : 'bg-red-100 text-red-800'}`}>{joining.status}</span>
                      </TableCell>
                      <TableCell>
                        <span className={`px-2 py-1 text-xs font-bold uppercase rounded ${joining.validated ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-600'}`} data-testid={`validation-status-${joining.id}`}>
                          {joining.validated ? 'Validated' : 'Pending'}
                        </span>
                        {joining.validated_by_name && <p className="text-xs text-slate-400 mt-1">by {joining.validated_by_name}</p>}
                      </TableCell>
                      <TableCell className="text-sm">{joining.recruiter_name}</TableCell>
                      <TableCell className="text-xs text-slate-600 max-w-[150px] truncate">{joining.remarks || <span className="text-slate-300">-</span>}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Button size="sm" variant="ghost" data-testid={`edit-joining-${joining.id}`} onClick={() => { setSelectedJoining(joining); setNewStatus(joining.status); setStatusDialogOpen(true); }} className="text-blue-600 hover:text-blue-900 hover:bg-blue-50" title="Edit Status"><Edit2 size={14} /></Button>
                          {canValidate && !joining.validated && (
                            <Button size="sm" variant="ghost" data-testid={`validate-joining-${joining.id}`} onClick={() => handleValidate(joining.id)} className="text-emerald-600 hover:text-emerald-900 hover:bg-emerald-50" title="Validate"><ShieldCheck size={14} /></Button>
                          )}
                          {isAdmin && <Button size="sm" variant="ghost" data-testid={`delete-joining-${joining.id}`} onClick={() => handleDeleteJoining(joining.id)} className="text-red-600 hover:text-red-800 hover:bg-red-50" title="Delete"><Trash2 size={14} /></Button>}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </>
      )}

      {activeTab === 'invoicing' && canSeeInvoicing && <InvoicingTab />}
    </div>
  );
}


/* ==================== INVOICING TAB ==================== */
function InvoicingTab() {
  const [invoices, setInvoices] = useState([]);
  const [summary, setSummary] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterClient, setFilterClient] = useState('');
  const [filterRecruiter, setFilterRecruiter] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [quickFilter, setQuickFilter] = useState('');
  const token = localStorage.getItem('token');
  const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  const fetchInvoices = async () => {
    try {
      let url = `${API}/invoices?status=${filterStatus}`;
      if (filterClient) url += `&client=${encodeURIComponent(filterClient)}`;
      if (filterRecruiter) url += `&recruiter=${encodeURIComponent(filterRecruiter)}`;
      if (filterDateFrom) url += `&date_from=${filterDateFrom}`;
      if (filterDateTo) url += `&date_to=${filterDateTo}`;
      const res = await axios.get(url, { headers: getHeaders() });
      setInvoices(res.data);
    } catch { toast.error('Failed to fetch invoices'); }
  };

  const fetchSummary = async () => {
    try {
      let url = `${API}/invoices/summary?`;
      if (filterDateFrom) url += `&date_from=${filterDateFrom}`;
      if (filterDateTo) url += `&date_to=${filterDateTo}`;
      const res = await axios.get(url, { headers: getHeaders() });
      setSummary(res.data);
    } catch (err) { console.error("./pages/Joinings.jsx:", err); }
  };

  useEffect(() => { fetchInvoices(); fetchSummary(); }, [filterStatus, filterClient, filterRecruiter, filterDateFrom, filterDateTo]);

  const applyQuickFilter = (period) => {
    const now = new Date();
    setQuickFilter(period);
    if (period === 'this_month') {
      setFilterDateFrom(new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10));
      setFilterDateTo(now.toISOString().slice(0, 10));
    } else if (period === 'last_month') {
      const first = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const last = new Date(now.getFullYear(), now.getMonth(), 0);
      setFilterDateFrom(first.toISOString().slice(0, 10));
      setFilterDateTo(last.toISOString().slice(0, 10));
    } else if (period === 'this_quarter') {
      const qMonth = Math.floor(now.getMonth() / 3) * 3;
      setFilterDateFrom(new Date(now.getFullYear(), qMonth, 1).toISOString().slice(0, 10));
      setFilterDateTo(now.toISOString().slice(0, 10));
    } else if (period === 'this_year') {
      setFilterDateFrom(new Date(now.getFullYear(), 0, 1).toISOString().slice(0, 10));
      setFilterDateTo(now.toISOString().slice(0, 10));
    } else {
      setFilterDateFrom(''); setFilterDateTo('');
    }
  };

  const startEdit = (inv) => {
    setEditingId(inv.id);
    setEditData({
      invoice_number: inv.invoice_number || '',
      invoice_date: inv.invoice_date || '',
      invoice_amount: inv.invoice_amount || 0,
      gst_amount: inv.gst_amount || 0,
      total_amount: inv.total_amount || 0,
      receipt_date: inv.receipt_date || '',
      payment_received: inv.payment_received || 0,
      notes: inv.notes || '',
    });
  };

  const saveEdit = async () => {
    try {
      await axios.put(`${API}/invoices/${editingId}`, editData, { headers: getHeaders() });
      toast.success('Invoice updated'); setEditingId(null);
      fetchInvoices(); fetchSummary();
    } catch { toast.error('Failed to update invoice'); }
  };

  const handleExportCSV = async () => {
    try {
      let url = `${API}/invoices/export?status=${filterStatus}`;
      if (filterDateFrom) url += `&date_from=${filterDateFrom}`;
      if (filterDateTo) url += `&date_to=${filterDateTo}`;
      const res = await axios.get(url, { headers, responseType: 'blob' });
      const blobUrl = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = blobUrl; link.setAttribute('download', 'invoices_export.csv');
      document.body.appendChild(link); link.click(); link.remove(); window.URL.revokeObjectURL(blobUrl);
      toast.success('Invoices exported');
    } catch { toast.error('Export failed'); }
  };

  const clearFilters = () => {
    setFilterStatus('all'); setFilterClient(''); setFilterRecruiter('');
    setFilterDateFrom(''); setFilterDateTo(''); setQuickFilter('');
  };

  const fmt = (n) => n ? `₹${Number(n).toLocaleString('en-IN', { minimumFractionDigits: 2 })}` : '₹0.00';

  // Client-side filtered summary
  const filteredSummary = useMemo(() => {
    let data = invoices;
    if (filterStatus !== 'all') data = data.filter(i => i.payment_status === filterStatus);
    return {
      count: data.length,
      invoice_amount: data.reduce((s, i) => s + (i.invoice_amount || 0), 0),
      gst: data.reduce((s, i) => s + (i.gst_amount || 0), 0),
      total: data.reduce((s, i) => s + (i.total_amount || 0), 0),
      received: data.reduce((s, i) => s + (i.payment_received || 0), 0),
    };
  }, [invoices, filterStatus]);

  return (
    <div>
      {/* Quick Filters */}
      <div className="flex flex-wrap gap-2 mb-4">
        {['all', 'this_month', 'last_month', 'this_quarter', 'this_year'].map(p => (
          <button key={p} data-testid={`quick-filter-${p}`} onClick={() => applyQuickFilter(p)}
            className={`px-3 py-1.5 text-xs font-bold rounded-full border transition-all ${quickFilter === p || (p === 'all' && !quickFilter) ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-slate-600 border-slate-200 hover:border-blue-300'}`}>
            {p === 'all' ? 'All Time' : p.replace(/_/g, ' ').replace(/^\w/, c => c.toUpperCase())}
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-white border border-blue-100 rounded shadow-sm p-4 mb-4 flex flex-wrap items-end gap-4">
        <div>
          <Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Status</Label>
          <select data-testid="inv-filter-status" value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="mt-1 flex h-9 rounded border border-blue-200 bg-white px-3 py-1 text-sm">
            <option value="all">All Status</option>
            <option value="unpaid">Unpaid</option>
            <option value="partial">Partial</option>
            <option value="paid">Paid</option>
          </select>
        </div>
        <div>
          <Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Client</Label>
          <Input data-testid="inv-filter-client" value={filterClient} onChange={e => setFilterClient(e.target.value)} placeholder="Search client..." className="mt-1 h-9 rounded border-blue-200 text-sm w-40" />
        </div>
        <div>
          <Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Recruiter</Label>
          <Input data-testid="inv-filter-recruiter" value={filterRecruiter} onChange={e => setFilterRecruiter(e.target.value)} placeholder="Search recruiter..." className="mt-1 h-9 rounded border-blue-200 text-sm w-40" />
        </div>
        <div>
          <Label className="text-xs font-bold uppercase tracking-wider text-blue-900">From</Label>
          <Input data-testid="inv-filter-from" type="date" value={filterDateFrom} onChange={e => setFilterDateFrom(e.target.value)} className="mt-1 h-9 rounded border-blue-200 text-sm" />
        </div>
        <div>
          <Label className="text-xs font-bold uppercase tracking-wider text-blue-900">To</Label>
          <Input data-testid="inv-filter-to" type="date" value={filterDateTo} onChange={e => setFilterDateTo(e.target.value)} className="mt-1 h-9 rounded border-blue-200 text-sm" />
        </div>
        <Button variant="ghost" size="sm" onClick={clearFilters} className="text-slate-500 text-xs"><X size={12} className="mr-1" />Clear</Button>
        <Button data-testid="export-invoices-csv" variant="outline" size="sm" onClick={handleExportCSV} className="border-blue-200 text-blue-700 text-xs ml-auto">
          <Download size={14} className="mr-1" />Export CSV
        </Button>
      </div>

      {/* Invoice Table */}
      <div className="bg-white border border-blue-100 rounded shadow-sm overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-blue-50 border-b border-blue-100">
              <TableHead className="font-bold text-blue-900 text-xs">Candidate</TableHead>
              <TableHead className="font-bold text-blue-900 text-xs">Position</TableHead>
              <TableHead className="font-bold text-blue-900 text-xs">Client</TableHead>
              <TableHead className="font-bold text-blue-900 text-xs">Recruiter</TableHead>
              <TableHead className="font-bold text-blue-900 text-xs">Joining Date</TableHead>
              <TableHead className="font-bold text-blue-900 text-xs">Invoice #</TableHead>
              <TableHead className="font-bold text-blue-900 text-xs">Invoice Date</TableHead>
              <TableHead className="font-bold text-blue-900 text-xs text-right">Invoice Amt</TableHead>
              <TableHead className="font-bold text-blue-900 text-xs text-right">GST</TableHead>
              <TableHead className="font-bold text-blue-900 text-xs text-right">Total</TableHead>
              <TableHead className="font-bold text-blue-900 text-xs">Receipt Date</TableHead>
              <TableHead className="font-bold text-blue-900 text-xs text-right">Received</TableHead>
              <TableHead className="font-bold text-blue-900 text-xs">Status</TableHead>
              <TableHead className="font-bold text-blue-900 text-xs">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {invoices.length === 0 ? (
              <TableRow><TableCell colSpan={14} className="text-center text-slate-500 py-8">No invoices yet. Validate joinings to create invoices.</TableCell></TableRow>
            ) : invoices.map((inv) => (
              <TableRow key={inv.id} data-testid={`invoice-row-${inv.id}`} className="hover:bg-blue-50/50">
                <TableCell className="text-sm font-semibold text-blue-900">{inv.candidate_name}</TableCell>
                <TableCell className="text-sm">{inv.position_title}</TableCell>
                <TableCell className="text-sm">{inv.client_name}</TableCell>
                <TableCell className="text-sm">{inv.recruiter_name}</TableCell>
                <TableCell className="text-sm">{inv.joining_date ? new Date(inv.joining_date).toLocaleDateString() : '-'}</TableCell>
                {editingId === inv.id ? (
                  <>
                    <TableCell><Input data-testid="edit-inv-number" value={editData.invoice_number} onChange={e => setEditData({...editData, invoice_number: e.target.value})} className="h-7 text-xs w-28 rounded" placeholder="INV-001" /></TableCell>
                    <TableCell><Input data-testid="edit-inv-date" type="date" value={editData.invoice_date} onChange={e => setEditData({...editData, invoice_date: e.target.value})} className="h-7 text-xs w-28 rounded" /></TableCell>
                    <TableCell><Input data-testid="edit-inv-amount" type="number" value={editData.invoice_amount} onChange={e => { const v = parseFloat(e.target.value) || 0; setEditData({...editData, invoice_amount: v, total_amount: Math.round((v + editData.gst_amount) * 100) / 100 }); }} className="h-7 text-xs w-24 rounded text-right" /></TableCell>
                    <TableCell><Input data-testid="edit-inv-gst" type="number" value={editData.gst_amount} onChange={e => { const v = parseFloat(e.target.value) || 0; setEditData({...editData, gst_amount: v, total_amount: Math.round((editData.invoice_amount + v) * 100) / 100 }); }} className="h-7 text-xs w-24 rounded text-right" /></TableCell>
                    <TableCell className="text-right font-bold text-sm">{fmt(editData.total_amount)}</TableCell>
                    <TableCell><Input data-testid="edit-inv-receipt-date" type="date" value={editData.receipt_date} onChange={e => setEditData({...editData, receipt_date: e.target.value})} className="h-7 text-xs w-28 rounded" /></TableCell>
                    <TableCell><Input data-testid="edit-inv-received" type="number" value={editData.payment_received} onChange={e => setEditData({...editData, payment_received: parseFloat(e.target.value) || 0})} className="h-7 text-xs w-24 rounded text-right" /></TableCell>
                    <TableCell>-</TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button size="sm" data-testid="save-invoice-btn" onClick={saveEdit} className="bg-emerald-600 hover:bg-emerald-700 text-white h-6 text-xs rounded px-2"><Save size={10} className="mr-1" />Save</Button>
                        <Button size="sm" variant="ghost" onClick={() => setEditingId(null)} className="h-6 text-xs">Cancel</Button>
                      </div>
                    </TableCell>
                  </>
                ) : (
                  <>
                    <TableCell className="text-sm font-mono" data-testid={`inv-number-${inv.id}`}>{inv.invoice_number || <span className="text-slate-300">-</span>}</TableCell>
                    <TableCell className="text-sm">{inv.invoice_date || <span className="text-slate-300">-</span>}</TableCell>
                    <TableCell className="text-sm text-right font-semibold">{inv.invoice_amount ? fmt(inv.invoice_amount) : <span className="text-slate-300">-</span>}</TableCell>
                    <TableCell className="text-sm text-right">{inv.gst_amount ? fmt(inv.gst_amount) : <span className="text-slate-300">-</span>}</TableCell>
                    <TableCell className="text-sm text-right font-bold text-blue-900">{inv.total_amount ? fmt(inv.total_amount) : <span className="text-slate-300">-</span>}</TableCell>
                    <TableCell className="text-sm">{inv.receipt_date || <span className="text-slate-300">-</span>}</TableCell>
                    <TableCell className="text-sm text-right font-semibold text-emerald-700">{inv.payment_received ? fmt(inv.payment_received) : <span className="text-slate-300">-</span>}</TableCell>
                    <TableCell>
                      <span className={`px-2 py-1 text-xs font-bold uppercase rounded ${inv.payment_status === 'paid' ? 'bg-emerald-100 text-emerald-800' : inv.payment_status === 'partial' ? 'bg-amber-100 text-amber-800' : 'bg-red-100 text-red-800'}`} data-testid={`inv-status-${inv.id}`}>
                        {inv.payment_status || 'unpaid'}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Button size="sm" variant="ghost" data-testid={`edit-invoice-${inv.id}`} onClick={() => startEdit(inv)} className="text-blue-600 hover:bg-blue-50 h-6 text-xs"><Edit2 size={12} /></Button>
                    </TableCell>
                  </>
                )}
              </TableRow>
            ))}
            {/* Summary Row */}
            {invoices.length > 0 && (
              <TableRow className="bg-blue-50 border-t-2 border-blue-200 font-bold" data-testid="invoice-summary-row">
                <TableCell colSpan={7} className="text-right text-sm text-blue-900 uppercase tracking-wider">Totals ({filteredSummary.count} invoices)</TableCell>
                <TableCell className="text-right text-sm text-blue-900">{fmt(filteredSummary.invoice_amount)}</TableCell>
                <TableCell className="text-right text-sm text-blue-900">{fmt(filteredSummary.gst)}</TableCell>
                <TableCell className="text-right text-sm text-blue-900">{fmt(filteredSummary.total)}</TableCell>
                <TableCell></TableCell>
                <TableCell className="text-right text-sm text-emerald-700">{fmt(filteredSummary.received)}</TableCell>
                <TableCell className="text-xs text-blue-900">Pending: {fmt(filteredSummary.total - filteredSummary.received)}</TableCell>
                <TableCell></TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
